import React, { useState, useCallback, useEffect, useMemo } from 'react';
import PageBanner from '../components/utils/PageBanner';
import DictionaryCard from '../components/DataDictionary/DictionaryCard';
import ErrorModal from '../components/DataDictionary/ErrorModal';
import SuccessModal from '../components/DataDictionary/SuccessModal';
import LoadingOverlay from '../components/DataDictionary/LoadingOverlay';
import { BookOpenIcon, ChevronUpIcon, ChevronDownIcon } from '@heroicons/react/24/solid';
import {
  useExportRecipes,
  useExportEvents,
  useExportSubevents,
  useExportProducts,
  useImportRecipes,
  useImportEvents,
  useImportSubevents,
  useImportProducts,
  useGenerateReleaseCandidate,
  useDeployReleaseCandidate,
} from '../hooks/useDataDictionary';
import { validateCsvFile, downloadFromPresignedUrl } from '../utils/dataDictionaryUtils';
import { useSnackbar } from '../providers/SnackbarContext';
import { useProject } from '../config/ProjectContext';
import usePermission from '../authorization/permissionHook';
import { useAuditLog } from '../hooks/useAuditLog';
import useAnalytics from '../hooks/useAnalytics';
import GraphqlService from '../service/graphqlService';
import {
  EXPORT_RECIPES_PERMISSION,
  IMPORT_RECIPES_PERMISSION,
  MOMENTO_PROJECT_ID,
} from '../utils/constants';
import { isProdEnvironment } from '../utils/environmentConfig';

/**
 * DataDictionary Screen - Main container component
 * Manages data dictionary import/export for Recipes, Events, Subevents, and Products
 * In non-production: shows Dictionaries and Snapshots tabs
 * In production: shows only Releases tab
 */
export default function DataDictionary() {
  // Get project from context
  const { project } = useProject();
  const projectId = project?.code;
  const openSnackBar = useSnackbar();

  // Tab state - default to 'releases' in production, 'dictionaries' in non-production
  const [activeTab, setActiveTab] = useState(isProdEnvironment() ? 'releases' : 'dictionaries');

  // Permission check using authorization hook
  const [loadingPermissions, hasPermission] = usePermission([
    EXPORT_RECIPES_PERMISSION,
    IMPORT_RECIPES_PERMISSION,
  ]);

  // For now, if user has either export or import permission, they can access
  const canExport = hasPermission;
  const canImport = hasPermission;

  // State for tracking current operations
  const [exportingTypes, setExportingTypes] = useState(new Set());
  const [importingTypes, setImportingTypes] = useState(new Set());

  // Query mutations
  const exportRecipesMutation = useExportRecipes();
  const exportEventsMutation = useExportEvents();
  const exportSubeventsMutation = useExportSubevents();
  const exportProductsMutation = useExportProducts();

  const importRecipesMutation = useImportRecipes();
  const importEventsMutation = useImportEvents();
  const importSubeventsMutation = useImportSubevents();
  const importProductsMutation = useImportProducts();

  // Release management mutations
  const generateReleaseCandidateMutation = useGenerateReleaseCandidate();
  const deployReleaseCandidateMutation = useDeployReleaseCandidate();

  // Audit logging (silent mode - errors only logged to console, not shown to user)
  const { logAuditChange } = useAuditLog({ silent: true });

  // Analytics
  const { setAnalytics } = useAnalytics();

  // Section loaded analytics
  useEffect(() => {
    setAnalytics({
      event: 'section_loaded',
      sectionId: 'device_management-data_dictionary',
    });
  }, [setAnalytics]);

  // Snapshot audit logs state
  const [snapshotAuditLogs, setSnapshotAuditLogs] = useState([]);
  const [snapshotLoading, setSnapshotLoading] = useState(false);

  // Releases state
  const [releasesList, setReleasesList] = useState([]);
  const [releasesLoading, setReleasesLoading] = useState(false);
  const [releasesError, setReleasesError] = useState(null);
  const [releasesSortOrder, setReleasesSortOrder] = useState('desc'); // 'desc' = newest first, 'asc' = oldest first

  // Sorted releases list (memoized)
  const sortedReleasesList = useMemo(() => {
    if (!releasesList || releasesList.length === 0) return [];
    return [...releasesList].sort((a, b) => {
      const dateA = new Date(a.last_modified || 0);
      const dateB = new Date(b.last_modified || 0);
      return releasesSortOrder === 'desc' ? dateB - dateA : dateA - dateB;
    });
  }, [releasesList, releasesSortOrder]);

  // Toggle sort order for releases
  const toggleReleasesSortOrder = useCallback(() => {
    setReleasesSortOrder((prev) => (prev === 'desc' ? 'asc' : 'desc'));
  }, []);

  // Dictionary audit logs state
  const [dictionaryAuditLogs, setDictionaryAuditLogs] = useState({
    recipes: null,
    events: null,
    subevents: null,
    products: null,
  });

  // Error modal state
  const [errorModal, setErrorModal] = useState({
    isOpen: false,
    title: '',
    message: '',
    details: '',
  });

  // Success modal state
  const [successModal, setSuccessModal] = useState({
    isOpen: false,
    title: '',
    message: '',
  });

  // Loading overlay state for long-running operations
  const [loadingOverlay, setLoadingOverlay] = useState({
    isLoading: false,
    title: '',
    message: '',
  });

  // Fetch all audit logs for dictionary operations
  const fetchDictionaryAuditLogs = useCallback(async () => {
    if (!projectId) return;
    
    try {
      const auditLogs = await GraphqlService.getAuditLogs({
        limit: 1000,
        page: 1,
        entity: 'DataDictionary',
        entity_id: String(projectId),
      });

      if (auditLogs && auditLogs.data) {
        // Find the latest import/export for each dictionary type
        const logs = {
          recipes: null,
          events: null,
          subevents: null,
          products: null,
        };

        // Sort logs by created_at descending to get most recent first
        const sortedLogs = auditLogs.data.sort((a, b) => {
          const dateA = new Date(a.created_at || 0);
          const dateB = new Date(b.created_at || 0);
          return dateB - dateA;
        });

        sortedLogs.forEach((log) => {
          if (log.change === 'ImportRecipes' && !logs.recipes) {
            logs.recipes = log;
          } else if (log.change === 'ImportEvents' && !logs.events) {
            logs.events = log;
          } else if (log.change === 'ImportSubevents' && !logs.subevents) {
            logs.subevents = log;
          } else if (log.change === 'ImportProducts' && !logs.products) {
            logs.products = log;
          }
        });

        setDictionaryAuditLogs(logs);
      }
    } catch (error) {
      console.error('Error fetching dictionary audit logs:', error);
    }
  }, [projectId]);

  // Fetch snapshot creation audit logs
  const fetchSnapshotAuditLogs = useCallback(async () => {
    if (!projectId) return;
    
    setSnapshotLoading(true);
    try {
      const auditLogs = await GraphqlService.getAuditLogs({
        limit: 100,
        page: 1,
        entity: 'DataDictionary',
        entity_id: String(projectId),
      });

      if (auditLogs && auditLogs.data) {
        // Filter for snapshot creation events
        const snapshotLogs = auditLogs.data.filter(
          log => log.change === 'SnapshotCreation'
        );
        setSnapshotAuditLogs(snapshotLogs);
      }
    } catch (error) {
      console.error('Error fetching snapshot audit logs:', error);
    } finally {
      setSnapshotLoading(false);
    }
  }, [projectId]);

  // Fetch releases list
  const fetchReleases = useCallback(async () => {
    if (!projectId) return;
    
    setReleasesLoading(true);
    setReleasesError(null);
    try {
      const result = await GraphqlService.getReleases({ projectId });
      if (result && result.releases) {
        setReleasesList(result.releases);
      } else {
        setReleasesList([]);
      }
    } catch (error) {
      console.error('Error fetching releases:', error);
      setReleasesList([]);
      setReleasesError(error.message || 'Failed to load releases');
      openSnackBar('Failed to load releases', 'error');
    } finally {
      setReleasesLoading(false);
    }
  }, [projectId, openSnackBar]);

  // Fetch data based on active tab and environment
  // In production: only Releases tab is available
  // In non-production: Dictionaries and Snapshots tabs are available
  useEffect(() => {
    if (isProdEnvironment()) {
      // Production: only fetch releases
      if (activeTab === 'releases') {
        fetchReleases();
      }
    } else {
      // Non-production: fetch dictionaries or snapshots
      if (activeTab === 'dictionaries') {
        fetchDictionaryAuditLogs();
      } else if (activeTab === 'snapshots') {
        fetchSnapshotAuditLogs();
      }
    }
  }, [activeTab, fetchDictionaryAuditLogs, fetchSnapshotAuditLogs, fetchReleases]);

  // Helper functions for export
  const handleExport = useCallback(
    async (type) => {
      setAnalytics({
        event: 'button_clicked',
        extraParams: { name: `device_management-data_dictionary-dictionaries-${type}-csv_download` }
      });

      if (!projectId) {
        openSnackBar('Project not selected', 'error');
        return;
      }

      if (!canExport) {
        openSnackBar('You do not have permission to export data', 'error');
        return;
      }

      setExportingTypes((prev) => new Set(prev).add(type));

      try {
        let mutation;
        let filename;

        switch (type) {
          case 'recipes':
            mutation = exportRecipesMutation;
            filename = 'recipes.csv';
            break;
          case 'events':
            mutation = exportEventsMutation;
            filename = 'events.csv';
            break;
          case 'subevents':
            mutation = exportSubeventsMutation;
            filename = 'subevents.csv';
            break;
          case 'products':
            mutation = exportProductsMutation;
            filename = 'products.csv';
            break;
          default:
            throw new Error('Unknown dictionary type');
        }

        const presignedUrl = await mutation.mutateAsync(projectId);
        await downloadFromPresignedUrl(presignedUrl, filename);
        openSnackBar(`${type.charAt(0).toUpperCase() + type.slice(1)} exported successfully`, 'success');
      } catch (error) {
        const errorMessage =
          error instanceof Error ? error.message : `Failed to export ${type}`;
        openSnackBar(errorMessage, 'error');
      } finally {
        setExportingTypes((prev) => {
          const newSet = new Set(prev);
          newSet.delete(type);
          return newSet;
        });
      }
    },
    [projectId, canExport, openSnackBar, setAnalytics, exportRecipesMutation, exportEventsMutation, exportSubeventsMutation, exportProductsMutation],
  );

  // Helper function for import
  const handleImport = useCallback(
    async (type, file) => {
      setAnalytics({
        event: 'button_clicked',
        extraParams: { name: `device_management-data_dictionary-dictionaries-${type}-csv_upload` }
      });

      if (!projectId) {
        openSnackBar('Project not selected', 'error');
        return;
      }

      if (!canImport) {
        openSnackBar('You do not have permission to import data', 'error');
        return;
      }

      // Validate file
      const validation = validateCsvFile(file);
      if (!validation.valid) {
        openSnackBar(validation.error || 'Invalid file', 'error');
        return;
      }

      setImportingTypes((prev) => new Set(prev).add(type));

      try {
        // Pass the File object directly to the mutation
        console.log('[DataDictionary] Importing file:', {
          type,
          fileName: file.name,
          fileSize: file.size,
        });

        let mutation;

        switch (type) {
          case 'recipes':
            mutation = importRecipesMutation;
            break;
          case 'events':
            mutation = importEventsMutation;
            break;
          case 'subevents':
            mutation = importSubeventsMutation;
            break;
          case 'products':
            mutation = importProductsMutation;
            break;
          default:
            throw new Error('Unknown dictionary type');
        }

        const result = await mutation.mutateAsync({ projectId, file });
        
        // Check if result is an error response
        if (result && result.__typename && ['BadRequest', 'InternalServerError', 'NotFound', 'Forbidden'].includes(result.__typename)) {
          const errorMessage = result.message || `Failed to import ${type}`;
          
          console.log('[DataDictionary] Error response:', {
            __typename: result.__typename,
            message: result.message,
            detailsType: typeof result.details,
            detailsLength: result.details?.length,
            detailsFirst100: result.details?.substring(0, 100)
          });
          
          // Determine if details are complex (multi-line or structured error info)
          const hasComplexDetails = result.details && (
            result.details.includes('\n') ||
            result.details.includes('Type of error:') ||
            result.details.includes('Description of error:') ||
            result.details.includes('SQL statement:') ||
            result.details.includes('CONTEXT:') ||
            result.details.length > 150
          );
          
          console.log('[DataDictionary] hasComplexDetails:', hasComplexDetails);
          
          // If there are complex/structured error details, show in modal
          if (hasComplexDetails) {
            console.log('[DataDictionary] Opening error modal with details');
            setErrorModal({
              isOpen: true,
              title: `Failed to import ${type.charAt(0).toUpperCase() + type.slice(1)}`,
              message: errorMessage,
              details: result.details,
            });
          } else if (result.details) {
            // Simple short details - show in snackbar
            openSnackBar(`${errorMessage}: ${result.details}`, 'error');
          } else {
            // No details - just show message
            openSnackBar(errorMessage, 'error');
          }
          return;
        }
        
        // Get current date and time for audit log
        const importDate = new Date().toISOString();
        
        // Log the import action to audit logs
        await logAuditChange(
          'DataDictionary',
          `Import${type.charAt(0).toUpperCase() + type.slice(1)}`,
          String(projectId),
          `${type.charAt(0).toUpperCase() + type.slice(1)} imported from file: ${file.name}`,
          importDate
        );
        
        // Refetch dictionary audit logs to show the new import
        await fetchDictionaryAuditLogs();
        
        openSnackBar(`${type.charAt(0).toUpperCase() + type.slice(1)} uploaded successfully`, 'success');
      } catch (error) {
        const errorMessage =
          error instanceof Error ? error.message : `Failed to import ${type}`;
        
        console.log('[DataDictionary] Caught error:', {
          errorType: typeof error,
          errorMessage,
          errorFirst200: errorMessage?.substring(0, 200)
        });
        
        // Try to parse error message if it contains JSON error details
        try {
          // The error message format is: "Failed to import events: {...json...}"
          // Extract the JSON part
          const jsonMatch = errorMessage.match(/\{[\s\S]*\}$/);
          if (jsonMatch) {
            const errorObj = JSON.parse(jsonMatch[0]);
            if (errorObj.message && errorObj.details) {
              const hasComplexDetails = errorObj.details && (
                errorObj.details.includes('\n') ||
                errorObj.details.includes('Type of error:') ||
                errorObj.details.includes('Description of error:') ||
                errorObj.details.includes('SQL statement:') ||
                errorObj.details.includes('CONTEXT:') ||
                errorObj.details.length > 150
              );
              
              if (hasComplexDetails) {
                console.log('[DataDictionary] Opening modal from catch block');
                setErrorModal({
                  isOpen: true,
                  title: `Failed to import ${type.charAt(0).toUpperCase() + type.slice(1)}`,
                  message: errorObj.message,
                  details: errorObj.details,
                });
                return;
              }
            }
          }
        } catch (parseError) {
          // Not a JSON error, continue with simple error display
          console.log('[DataDictionary] Could not parse error as JSON:', parseError.message);
        }
        
        openSnackBar(errorMessage, 'error');
      } finally {
        setImportingTypes((prev) => {
          const newSet = new Set(prev);
          newSet.delete(type);
          return newSet;
        });
      }
    },
    [
      projectId,
      canImport,
      openSnackBar,
      setAnalytics,
      importRecipesMutation,
      importEventsMutation,
      importSubeventsMutation,
      importProductsMutation,
      logAuditChange,
      fetchDictionaryAuditLogs,
    ],
  );

  // Handler for creating a snapshot/release candidate
  const handleCreateSnapshot = useCallback(
    async () => {
      setAnalytics({
        event: 'button_clicked',
        extraParams: { name: 'device_management-data_dictionary-dictionaries-create_snapshot' }
      });

      if (!projectId) {
        openSnackBar('Project not selected', 'error');
        return;
      }

      // Show loading overlay
      setLoadingOverlay({
        isLoading: true,
        title: 'Creating Snapshot',
        message: 'Please wait while we create your data dictionary snapshot. This may take a few moments...',
      });

      try {
        await generateReleaseCandidateMutation.mutateAsync(projectId);
        
        // Get current date and time
        const createdDate = new Date().toISOString();
        
        // Log the snapshot creation to audit logs with the creation date
        await logAuditChange(
          'DataDictionary',
          'SnapshotCreation',
          String(projectId),
          'Data Dictionary Snapshot Created',
          createdDate
        );

        // Hide loading overlay
        setLoadingOverlay({ isLoading: false, title: '', message: '' });

        // Show success modal with production deployment notice
        setSuccessModal({
          isOpen: true,
          title: 'Snapshot Created Successfully',
          message: 'Your snapshot has been created. To make this snapshot visible in Production, you must go to the Production environment and deploy it there.',
        });
        
        // Refetch snapshot logs if we're on the snapshots tab
        if (activeTab === 'snapshots') {
          fetchSnapshotAuditLogs();
        }
      } catch (error) {
        // Hide loading overlay
        setLoadingOverlay({ isLoading: false, title: '', message: '' });
        const errorMessage =
          error instanceof Error ? error.message : 'Failed to create snapshot';
        openSnackBar(errorMessage, 'error');
      }
    },
    [projectId, openSnackBar, setAnalytics, generateReleaseCandidateMutation, logAuditChange, activeTab, fetchSnapshotAuditLogs],
  );

  // Handler for deploying the release candidate
  const handleDeployRelease = useCallback(
    async () => {
      setAnalytics({
        event: 'button_clicked',
        extraParams: { name: 'device_management-data_dictionary-deploy_release' }
      });

      if (!projectId) {
        openSnackBar('Project not selected', 'error');
        return;
      }

      if (!releasesList || releasesList.length === 0) {
        openSnackBar('No release candidate available to deploy', 'warning');
        return;
      }

      // Show loading overlay
      setLoadingOverlay({
        isLoading: true,
        title: 'Deploying Release',
        message: 'Please wait while we release your snapshot. This may take a moment…',
      });

      try {
        const result = await deployReleaseCandidateMutation.mutateAsync(projectId);
        
        // Hide loading overlay
        setLoadingOverlay({ isLoading: false, title: '', message: '' });

        // Check if the result contains an error (GraphQL error response)
        if (result && result.__typename && result.__typename !== 'DeployReleaseCandidateSuccess') {
          // Error response from GraphQL (e.g., NotFound, BadRequest, InternalServerError)
          const errorMessage = result.message || 'Failed to deploy release candidate';
          openSnackBar(errorMessage, 'error');
          return;
        }
        
        // Log the deployment to audit logs (silent mode - errors only in console)
        logAuditChange(
          'DataDictionary',
          'DeploymentCreation',
          String(projectId),
          'Data Dictionary Release Deployed',
          new Date().toISOString()
        );

        openSnackBar('Snapshot released successfully', 'success');
        
        // Refetch releases list to show the newly deployed version
        fetchReleases();
      } catch (error) {
        // Hide loading overlay
        setLoadingOverlay({ isLoading: false, title: '', message: '' });
        const errorMessage =
          error instanceof Error ? error.message : 'Failed to deploy release candidate';
        openSnackBar(errorMessage, 'error');
      }
    },
    [projectId, openSnackBar, setAnalytics, deployReleaseCandidateMutation, logAuditChange, releasesList, fetchReleases],
  );

  // No project selected message
  if (!projectId) {
    return (
      <div className="min-h-screen bg-gray-50">
        <PageBanner title="Data Dictionary" />
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
          <div className="flex justify-end mb-6">
            <button
              type="button"
              onClick={() => window.open('https://nestle.atlassian.net/wiki/spaces/DIIS/pages/725090486/Data+Dictionary+Management?atlOrigin=eyJpIjoiM2ExODU1MTRhMzFkNGUyMTk0MWM2NjU0OWFiMGNlZDUiLCJwIjoiYyJ9', '_blank', 'noopener,noreferrer')}
              className="inline-flex items-center text-indigo-600 hover:text-indigo-800 text-sm font-medium"
            >
              <BookOpenIcon className="h-5 w-5 mr-2" /> How to use Data Dictionary
            </button>
          </div>
          <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-6">
            <p className="text-yellow-800">
              Please select a project to manage its data dictionary.
            </p>
          </div>
        </div>
      </div>
    );
  }

  // Loading permissions
  if (loadingPermissions) {
    return (
      <div className="min-h-screen bg-gray-50">
        <PageBanner title="Data Dictionary" />
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
          <div className="flex justify-end mb-6">
            <button
              type="button"
              onClick={() => window.open('https://nestle.atlassian.net/wiki/spaces/DIIS/pages/725090486/Data+Dictionary+Management?atlOrigin=eyJpIjoiM2ExODU1MTRhMzFkNGUyMTk0MWM2NjU0OWFiMGNlZDUiLCJwIjoiYyJ9', '_blank', 'noopener,noreferrer')}
              className="inline-flex items-center text-indigo-600 hover:text-indigo-800 text-sm font-medium"
            >
              <BookOpenIcon className="h-5 w-5 mr-2" /> How to use Data Dictionary
            </button>
          </div>
          <div className="text-center">
            <p className="text-gray-600">Loading...</p>
          </div>
        </div>
      </div>
    );
  }

  // No permission
  if (!hasPermission) {
    return (
      <div className="min-h-screen bg-gray-50">
        <PageBanner title="Data Dictionary" />
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
          <div className="flex justify-end mb-6">
            <button
              type="button"
              onClick={() => window.open('https://nestle.atlassian.net/wiki/spaces/DIIS/pages/725090486/Data+Dictionary+Management?atlOrigin=eyJpIjoiM2ExODU1MTRhMzFkNGUyMTk0MWM2NjU0OWFiMGNlZDUiLCJwIjoiYyJ9', '_blank', 'noopener,noreferrer')}
              className="inline-flex items-center text-indigo-600 hover:text-indigo-800 text-sm font-medium"
            >
              <BookOpenIcon className="h-5 w-5 mr-2" /> How to use Data Dictionary
            </button>
          </div>
          <div className="bg-red-50 border border-red-200 rounded-lg p-6">
            <p className="text-red-800">
              You do not have permission to access the Data Dictionary.
            </p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Error Modal */}
      <ErrorModal
        isOpen={errorModal.isOpen}
        title={errorModal.title}
        message={errorModal.message}
        details={errorModal.details}
        onClose={() => setErrorModal({ ...errorModal, isOpen: false })}
      />

      {/* Success Modal */}
      <SuccessModal
        isOpen={successModal.isOpen}
        title={successModal.title}
        message={successModal.message}
        onClose={() => setSuccessModal({ ...successModal, isOpen: false })}
        onCloseClick={() => setAnalytics({
          event: 'button_clicked',
          extraParams: { name: 'device_management-data_dictionary-dictionaries-snapshot_created-close' }
        })}
        onOkClick={() => setAnalytics({
          event: 'button_clicked',
          extraParams: { name: 'device_management-data_dictionary-dictionaries-snapshot_created-ok' }
        })}
        buttonText="Got it"
      />

      {/* Loading Overlay for long-running operations */}
      <LoadingOverlay
        isLoading={loadingOverlay.isLoading}
        title={loadingOverlay.title}
        message={loadingOverlay.message}
      />

      {/* Page Banner */}
      <PageBanner title="Data Dictionary" />

      {/* Main Content */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
        {/* Contextual Guide Link */}
        <div className="flex justify-end mb-6">
          <button
            type="button"
            onClick={() => {
              setAnalytics({
                event: 'button_clicked',
                extraParams: { name: 'device_management-data_dictionary-how_to_use' }
              });
              window.open('https://nestle.atlassian.net/wiki/spaces/DIIS/pages/725090486/Data+Dictionary+Management?atlOrigin=eyJpIjoiM2ExODU1MTRhMzFkNGUyMTk0MWM2NjU0OWFiMGNlZDUiLCJwIjoiYyJ9', '_blank', 'noopener,noreferrer');
            }}
            className="inline-flex items-center text-indigo-600 hover:text-indigo-800 text-sm font-medium"
          >
            <BookOpenIcon className="h-5 w-5 mr-2" /> How to use Data Dictionary
          </button>
        </div>
        {/* Tabs Navigation */}
        <div className="border-b border-gray-200 mb-8">
          <div className="flex gap-0">
            {/* Dictionaries Tab - Only visible in non-production environments */}
            {!isProdEnvironment() && (
              <button
                onClick={() => {
                  setAnalytics({
                    event: 'button_clicked',
                    extraParams: { name: 'device_management-data_dictionary-dictionaries' }
                  });
                  setActiveTab('dictionaries');
                }}
                className={`px-6 py-3 font-medium text-sm transition-colors border-b-2 ${
                  activeTab === 'dictionaries'
                    ? 'border-indigo-600 text-indigo-600'
                    : 'border-transparent text-gray-600 hover:text-gray-900'
                }`}
              >
                Dictionaries
              </button>
            )}

            {/* Snapshots Tab - Only visible in non-production environments */}
            {!isProdEnvironment() && (
              <button
                onClick={() => {
                  setAnalytics({
                    event: 'button_clicked',
                    extraParams: { name: 'device_management-data_dictionary-snapshots' }
                  });
                  setActiveTab('snapshots');
                }}
                className={`px-6 py-3 font-medium text-sm transition-colors border-b-2 ${
                  activeTab === 'snapshots'
                    ? 'border-indigo-600 text-indigo-600'
                    : 'border-transparent text-gray-600 hover:text-gray-900'
                }`}
              >
                Snapshots
              </button>
            )}

            {/* Releases Tab - Only visible in Production */}
            {isProdEnvironment() && (
              <button
                onClick={() => setActiveTab('releases')}
                className={`px-6 py-3 font-medium text-sm transition-colors border-b-2 ${
                  activeTab === 'releases'
                    ? 'border-indigo-600 text-indigo-600'
                    : 'border-transparent text-gray-600 hover:text-gray-900'
                }`}
              >
                Releases
              </button>
            )}
          </div>
        </div>

        {/* Tab Content */}
        {/* Dictionaries Tab Content - Only in non-production */}
        {activeTab === 'dictionaries' && !isProdEnvironment() && (
          <>
            {/* Dictionaries Grid */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              {/* Recipes */}
              <DictionaryCard
                title="Recipes"
                uploadedDate={dictionaryAuditLogs.recipes?.value || null}
                canExport={canExport}
                canImport={canImport}
                onExport={() => handleExport('recipes')}
                onImport={(file) => handleImport('recipes', file)}
                onInfoClick={() => setAnalytics({
                  event: 'button_clicked',
                  extraParams: { name: 'device_management-data_dictionary-dictionaries-recipes-info' }
                })}
                onPreviewClose={() => setAnalytics({
                  event: 'button_clicked',
                  extraParams: { name: 'device_management-data_dictionary-dictionaries-recipes-csv_upload-close' }
                })}
                onPreviewCancel={() => setAnalytics({
                  event: 'button_clicked',
                  extraParams: { name: 'device_management-data_dictionary-dictionaries-recipes-csv_upload-cancel' }
                })}
                onPreviewUpload={() => setAnalytics({
                  event: 'button_clicked',
                  extraParams: { name: 'device_management-data_dictionary-dictionaries-recipes-csv_upload-upload' }
                })}
                isExporting={exportingTypes.has('recipes')}
                isImporting={importingTypes.has('recipes')}
                disabled={!projectId}
              />

              {/* Products */}
              <DictionaryCard
                title="Products"
                uploadedDate={dictionaryAuditLogs.products?.value || null}
                canExport={canExport}
                canImport={canImport}
                onExport={() => handleExport('products')}
                onImport={(file) => handleImport('products', file)}
                onInfoClick={() => setAnalytics({
                  event: 'button_clicked',
                  extraParams: { name: 'device_management-data_dictionary-dictionaries-products-info' }
                })}
                onPreviewClose={() => setAnalytics({
                  event: 'button_clicked',
                  extraParams: { name: 'device_management-data_dictionary-dictionaries-products-csv_upload-close' }
                })}
                onPreviewCancel={() => setAnalytics({
                  event: 'button_clicked',
                  extraParams: { name: 'device_management-data_dictionary-dictionaries-products-csv_upload-cancel' }
                })}
                onPreviewUpload={() => setAnalytics({
                  event: 'button_clicked',
                  extraParams: { name: 'device_management-data_dictionary-dictionaries-products-csv_upload-upload' }
                })}
                isExporting={exportingTypes.has('products')}
                isImporting={importingTypes.has('products')}
                disabled={!projectId}
              />

              {/* Events */}
              <DictionaryCard
                title="Events"
                uploadedDate={dictionaryAuditLogs.events?.value || null}
                canExport={canExport}
                canImport={canImport}
                onExport={() => handleExport('events')}
                onImport={(file) => handleImport('events', file)}
                onInfoClick={() => setAnalytics({
                  event: 'button_clicked',
                  extraParams: { name: 'device_management-data_dictionary-dictionaries-events-info' }
                })}
                onPreviewClose={() => setAnalytics({
                  event: 'button_clicked',
                  extraParams: { name: 'device_management-data_dictionary-dictionaries-events-csv_upload-close' }
                })}
                onPreviewCancel={() => setAnalytics({
                  event: 'button_clicked',
                  extraParams: { name: 'device_management-data_dictionary-dictionaries-events-csv_upload-cancel' }
                })}
                onPreviewUpload={() => setAnalytics({
                  event: 'button_clicked',
                  extraParams: { name: 'device_management-data_dictionary-dictionaries-events-csv_upload-upload' }
                })}
                isExporting={exportingTypes.has('events')}
                isImporting={importingTypes.has('events')}
                disabled={!projectId}
              />

              {/* Sub Events - Only visible for Momento project */}
              {projectId === MOMENTO_PROJECT_ID && (
                <DictionaryCard
                  title="Sub Events"
                  uploadedDate={dictionaryAuditLogs.subevents?.value || null}
                  canExport={canExport}
                  canImport={canImport}
                  onExport={() => handleExport('subevents')}
                  onImport={(file) => handleImport('subevents', file)}
                  onInfoClick={() => setAnalytics({
                    event: 'button_clicked',
                    extraParams: { name: 'device_management-data_dictionary-dictionaries-subevents-info' }
                  })}
                  onPreviewClose={() => setAnalytics({
                    event: 'button_clicked',
                    extraParams: { name: 'device_management-data_dictionary-dictionaries-subevents-csv_upload-close' }
                  })}
                  onPreviewCancel={() => setAnalytics({
                    event: 'button_clicked',
                    extraParams: { name: 'device_management-data_dictionary-dictionaries-subevents-csv_upload-cancel' }
                  })}
                  onPreviewUpload={() => setAnalytics({
                    event: 'button_clicked',
                    extraParams: { name: 'device_management-data_dictionary-dictionaries-subevents-csv_upload-upload' }
                  })}
                  isExporting={exportingTypes.has('subevents')}
                  isImporting={importingTypes.has('subevents')}
                  disabled={!projectId}
                />
              )}
            </div>

            {/* Create Snapshot Button */}
            <div className="flex justify-end mt-8">
              <button
                onClick={handleCreateSnapshot}
                disabled={generateReleaseCandidateMutation.isLoading || !projectId}
                className="bg-indigo-600 hover:bg-indigo-700 disabled:opacity-50 disabled:cursor-not-allowed text-white font-medium py-2 px-6 rounded-lg transition-colors focus:outline-none focus:ring-2 focus:ring-indigo-500"
              >
                {generateReleaseCandidateMutation.isLoading ? 'Creating...' : 'Create Snapshot'}
              </button>
            </div>
          </>
        )}

        {/* Snapshots Tab Content - Only in non-production */}
        {activeTab === 'snapshots' && !isProdEnvironment() && (
          <div>
            {snapshotLoading ? (
              <div className="flex items-center justify-center py-12">
                <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-indigo-500 mr-4" />
                <p className="text-gray-600 text-lg">Loading snapshots...</p>
              </div>
            ) : snapshotAuditLogs.length === 0 ? (
              <div className="text-center py-12 bg-white rounded-lg border border-gray-200">
                <p className="text-gray-500 text-lg">No snapshots created yet</p>
                <p className="text-gray-400 text-sm mt-2">
                  Create your first snapshot from the Dictionaries tab
                </p>
              </div>
            ) : (
              <div className="bg-white rounded-lg shadow overflow-hidden">
                {/* Table Header */}
                <div className="hidden md:grid md:grid-cols-3 bg-gray-100 border-b border-gray-200">
                  <div className="px-6 py-4 font-semibold text-gray-900">Created On</div>
                  <div className="px-6 py-4 font-semibold text-gray-900">Created By</div>
                  <div className="px-6 py-4 font-semibold text-gray-900">Details</div>
                </div>

                {/* Table Body */}
                <div className="divide-y divide-gray-200">
                  {snapshotAuditLogs.map((log, index) => {
                    // Use the value field which contains the ISO date we stored
                    const createdDate = log.value 
                      ? new Date(log.value).toLocaleString() 
                      : (log.created_at ? new Date(log.created_at).toLocaleString() : 'Unknown Date');
                    const createdBy = log.user_name || log.user_id || 'Unknown User';
                    const detail = log.detail || 'Data Dictionary Snapshot';

                    return (
                      <div 
                        key={index} 
                        className="grid grid-cols-1 md:grid-cols-3 gap-4 md:gap-0 px-6 py-4 hover:bg-gray-50 items-center"
                      >
                        {/* Mobile Label */}
                        <div className="md:hidden">
                          <p className="text-xs font-semibold text-gray-600 uppercase">Created On</p>
                        </div>
                        {/* Created Date */}
                        <div className="text-gray-900 text-sm">
                          {createdDate}
                        </div>

                        {/* Mobile Label */}
                        <div className="md:hidden">
                          <p className="text-xs font-semibold text-gray-600 uppercase">Created By</p>
                        </div>
                        {/* Created By */}
                        <div className="text-gray-900 text-sm">
                          {createdBy}
                        </div>

                        {/* Mobile Label */}
                        <div className="md:hidden">
                          <p className="text-xs font-semibold text-gray-600 uppercase">Details</p>
                        </div>
                        {/* Details */}
                        <div className="text-gray-600 text-sm">
                          {detail}
                        </div>
                      </div>
                    );
                  })}
                </div>
              </div>
            )}
          </div>
        )}

        {/* Releases Tab Content - Only visible in Production */}
        {isProdEnvironment() && activeTab === 'releases' && (
          <div>
            {/* Subtitle */}
            <div className="mb-6">
              <h2 className="text-lg font-semibold text-gray-900">Release Snapshot</h2>
              <p className="text-gray-600 text-sm mt-1">Release the latest snapshot to Production</p>
            </div>

            {/* Release Snapshot Button */}
            <div className="mb-6 flex justify-start">
              <button
                onClick={handleDeployRelease}
                disabled={deployReleaseCandidateMutation.isLoading || !projectId || !releasesList || releasesList.length === 0}
                className="bg-green-600 hover:bg-green-700 disabled:opacity-50 disabled:cursor-not-allowed text-white font-medium py-2 px-6 rounded-lg transition-colors focus:outline-none focus:ring-2 focus:ring-green-500"
                title="Release the latest snapshot to Production"
              >
                {deployReleaseCandidateMutation.isLoading ? 'Releasing...' : 'Release Snapshot'}
              </button>
            </div>

            {releasesLoading ? (
              <div className="flex items-center justify-center py-12">
                <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-indigo-500 mr-4" />
                <p className="text-gray-600 text-lg">Loading releases...</p>
              </div>
            ) : releasesError ? (
              <div className="bg-red-50 border border-red-200 rounded-lg p-6">
                <p className="text-red-700 font-medium">Failed to Load Releases</p>
                <p className="text-red-600 text-sm mt-2">{releasesError}</p>
                <button
                  onClick={() => fetchReleases()}
                  className="mt-4 bg-red-600 hover:bg-red-700 text-white font-medium py-2 px-4 rounded-lg transition-colors"
                >
                  Try Again
                </button>
              </div>
            ) : releasesList.length === 0 ? (
              <div className="text-center py-12 bg-white rounded-lg border border-gray-200">
                <p className="text-gray-500 text-lg">No releases found</p>
                <p className="text-gray-400 text-sm mt-2">
                  Create a snapshot from the Dictionaries tab to generate a release
                </p>
              </div>
            ) : (
              <div className="bg-white rounded-lg shadow overflow-hidden">
                {/* Table Header */}
                <div className="hidden md:grid md:grid-cols-2 bg-gray-100 border-b border-gray-200">
                  <div className="px-6 py-4 font-semibold text-gray-900">Version</div>
                  <div
                    className="px-6 py-4 font-semibold text-gray-900 cursor-pointer hover:bg-gray-200 transition-colors flex items-center gap-1 select-none"
                    onClick={toggleReleasesSortOrder}
                    title={releasesSortOrder === 'desc' ? 'Sorted by newest first. Click to sort by oldest first.' : 'Sorted by oldest first. Click to sort by newest first.'}
                  >
                    Released on
                    {releasesSortOrder === 'desc' ? (
                      <ChevronDownIcon className="h-4 w-4 text-gray-600" />
                    ) : (
                      <ChevronUpIcon className="h-4 w-4 text-gray-600" />
                    )}
                  </div>
                </div>

                {/* Table Body */}
                <div className="divide-y divide-gray-200">
                  {sortedReleasesList.map((release, index) => {
                    const lastModifiedDate = release.last_modified
                      ? new Date(release.last_modified).toLocaleString()
                      : 'Unknown Date';

                    return (
                      <div
                        key={index}
                        className="grid grid-cols-1 md:grid-cols-2 gap-4 md:gap-0 px-6 py-4 hover:bg-gray-50 items-center"
                      >
                        {/* Mobile Label */}
                        <div className="md:hidden">
                          <p className="text-xs font-semibold text-gray-600 uppercase">Version</p>
                        </div>
                        {/* Version */}
                        <div className="text-gray-900 text-sm font-medium">
                          v{release.version}
                        </div>

                        {/* Mobile Label */}
                        <div className="md:hidden">
                          <p className="text-xs font-semibold text-gray-600 uppercase">Released on</p>
                        </div>
                        {/* Released on */}
                        <div className="text-gray-600 text-sm">
                          {lastModifiedDate}
                        </div>
                      </div>
                    );
                  })}
                </div>
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
}
